import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Mail, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Commercial Growth",
  "Real Estate Support",
  "Sales Training",
  "Marketing & Content",
  "Business Growth & Strategy",
  "General Enquiry",
];

const initial = { name: "", email: "", phone: "", company: "", service: "General Enquiry", message: "" };

const inputCls =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        company: form.company || null,
        service: form.service,
        message: form.message,
      });
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm(initial);
      setSent(true);
      setTimeout(() => setSent(false), 6000);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-slate-50/70 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200/60 inline-block">
              Get in Touch
            </span>
          </Reveal>
          <Reveal>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              Ready to move forward? Let's talk growth.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md">
              Tell us where your business is at and where you want it to go. We'll
              come back with a practical plan — not a pitch deck.
            </p>
          </Reveal>
          <Reveal>
            <div className="mt-10 space-y-5">
              <a data-testid="contact-email-link" href="mailto:antonette.ram@hotmail.co.nz" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-emerald-600" />
                </span>
                <span>
                  <span className="block text-xs font-semibold text-slate-400 uppercase tracking-widest">Email us</span>
                  <span className="text-slate-800 font-medium group-hover:text-emerald-600 transition-colors break-all">
                    antonette.ram@hotmail.co.nz
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                </span>
                <span>
                  <span className="block text-xs font-semibold text-slate-400 uppercase tracking-widest">Based in</span>
                  <span className="text-slate-800 font-medium">New Zealand | Australia</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-10 shadow-xl shadow-slate-900/5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-name" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Full Name *</label>
                  <input id="cf-name" data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Email Address *</label>
                  <input id="cf-email" data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Phone Number</label>
                  <input id="cf-phone" data-testid="contact-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+64 ..." className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-company" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Company / Organisation</label>
                  <input id="cf-company" data-testid="contact-form-company-input" value={form.company} onChange={set("company")} placeholder="Your business" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-service" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Service Required</label>
                  <select id="cf-service" data-testid="contact-form-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Message *</label>
                  <textarea id="cf-message" data-testid="contact-form-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your business and what growth looks like for you..." className={`${inputCls} resize-none`} />
                </div>
              </div>

              <button
                data-testid="contact-form-submit-btn"
                type="submit"
                disabled={sending}
                className="group mt-8 w-full sm:w-auto flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-xl px-10 py-4 shadow-lg shadow-emerald-600/25 transition-all hover:shadow-xl"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                  </>
                ) : sent ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" /> Enquiry Sent
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-4 text-xs text-slate-400">
                Your enquiry goes straight to Antonette — expect a personal reply, not an autoresponder.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
